import moment from 'moment';
import { createBlobObj, handleDownload, handleShare } from '../../helper';
import {
  ErrorVariants,
  Invoice,
  PublishMessagePayload,
  dispatch,
  getSelectedItems,
  getWorkspaceId,
  showToast,
  useAppSelector,
  useLazyGetInvoiceDetailsQuery,
  usePublishMessageMutation,
} from '@zonofi/zono-money-store';
import { useCallback, useEffect, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { MessageType } from '@zonofi/common';
import JSZip from 'jszip';

export const useInvoiceDownload = (id?: string) => {
  const selectedInvoicesMap = useAppSelector(getSelectedItems);
  const sellerWorkspaceId = useAppSelector(getWorkspaceId);

  const [sendInvoiceApi, sendInvoiceApiResponse] = usePublishMessageMutation();

  const [invoiceDocBlob, setInvoiceDocBlob] = useState<Blob | null | undefined>(
    null
  );
  const [showSendInvoiceSuccessModal, setShowSendInvoiceSuccessModal] =
    useState<boolean>(false);
  const [isShareLoading, setIsShareLoading] = useState<boolean>(false);

  const [fetchInvoiceApi, fetchInvoiceApiResponse] =
    useLazyGetInvoiceDetailsQuery();

  const handleInvoiceDownload = async (
    invoiceDocUrl: string,
    fileName?: string
  ) => {
    if (!invoiceDocUrl) return;
    let invoiceDocBlobFromUrl: Blob | undefined;
    if (invoiceDocUrl) {
      invoiceDocBlobFromUrl = await createBlobObj(invoiceDocUrl);
    }
    if (id && invoiceDocBlobFromUrl)
      handleDownload(
        invoiceDocBlobFromUrl,
        fileName ?? `invoice_${moment(Date.now()).format('DD-MM-YYYY')}.pdf`,
        [
          () =>
            dispatch(
              showToast({
                message: `Downloaded successfully`,
                type: ErrorVariants.SUCCESS,
                canDisplay: true,
                responseStatus: 'Success',
              })
            ),
        ]
      );
  };

  const handleSendBulkInvoice = async () => {
    if (selectedInvoicesMap?.length === 0) return;
    const payload: PublishMessagePayload[] = selectedInvoicesMap.map(
      (invoice: Invoice) => {
        const messageId = uuidv4();
        const inviteIds = [invoice.inviteId];
        const newData = [
          {
            id: invoice.id,
            docNumber: invoice.docNumber,
            dueDate: invoice.dueDate,
            invoiceAmount: Number(invoice.invoiceAmount),
            docs: invoice.docs?.map((doc) => doc.url).filter(Boolean) || [],
            invoiceDate: invoice.invoiceDate,
          },
        ];

        return {
          sellerWorkspaceId,
          inviteIds,
          message: {
            type: MessageType.INVOICE_LIST,
            _id: messageId,
            cAt: moment().valueOf().toString(),
            chnType: 'C',
            _v: 2,
            source: 'system',
            data: newData,
          },
        } as PublishMessagePayload;
      }
    );

    try {
      await Promise.all(payload.map((request) => sendInvoiceApi(request))).then(
        () => {
          setShowSendInvoiceSuccessModal(true);
        }
      );
    } catch (error) {
      console.error('Failed to send some invoices:', error);
    }
  };

  const updateBlobStateAndGetInvoiceBlob = useCallback(
    async (id: string) => {
      try {
        const invoice = await fetchInvoiceApi({
          invoiceId: id,
          workspaceId: sellerWorkspaceId,
        })?.unwrap();

        let documentUrl;

        if (
          invoice?.docs &&
          Array.isArray(invoice.docs) &&
          invoice.docs.length > 0
        ) {
          const selectedDoc =
            invoice.docs.length > 1
              ? invoice.docs.find((doc) => doc?.subtype === 'official')
              : invoice.docs[0];

          if (selectedDoc?.url) {
            documentUrl = selectedDoc.url;
          }
        }

        if (!documentUrl) {
          return undefined;
        }

        const blob = await createBlobObj(documentUrl);
        setInvoiceDocBlob(blob);
        return blob;
      } catch (error) {
        console.error('Failed to fetch invoice or create blob:', error);
        return undefined;
      }
    },
    [sellerWorkspaceId]
  );

  const handleShareBulkInvoices = useCallback(
    async (invoiceIds: string[]) => {
      try {
        // Validate input
        if (!invoiceIds.length) {
          console.warn('No invoices selected for bulk share');
          return;
        }

        // Create a new ZIP instance
        const zip = new JSZip();

        // Fetch all invoices and their documents concurrently
        const invoicePromises = invoiceIds.map(async (id) => {
          try {
            setIsShareLoading(true);
            const invoice = await fetchInvoiceApi({
              invoiceId: id,
              workspaceId: sellerWorkspaceId,
            })?.unwrap();

            // Skip if no invoice found
            if (!invoice) {
              console.warn(`Invoice ${id} not found`);
              return null;
            }

            // Find the document to include
            let documentUrl = '';
            if (
              invoice?.docs &&
              Array.isArray(invoice.docs) &&
              invoice.docs.length > 0
            ) {
              const selectedDoc =
                invoice.docs.length > 1
                  ? invoice.docs.find((doc) => doc?.subtype === 'official')
                  : invoice.docs[0];

              documentUrl = selectedDoc?.url || '';
            }

            // Skip if no document URL
            if (!documentUrl) {
              console.warn(`No document found for invoice ${id}`);
              return null;
            }

            // Fetch the document blob
            const blob = await createBlobObj(documentUrl);

            // Create a safe filename
            const invoiceNumber = invoice.docNumber ?? `invoice_${id}`;
            const safeFileName = `${invoiceNumber.replace(/[^\w\s.-]/gi, '_')}.pdf`;

            return {
              id,
              blob,
              fileName: safeFileName,
              invoiceNumber,
            };
          } catch (error) {
            console.error(`Failed to fetch invoice ${id}:`, error);
            return null;
          }
        });

        // Wait for all invoices to be fetched
        const invoiceResults = await Promise.all(invoicePromises);

        // Filter out failed/null results
        const validInvoices = invoiceResults
          .filter(
            (result): result is NonNullable<typeof result> =>
              result !== null && result.blob !== undefined
          )
          .filter(Boolean);

        if (validInvoices.length === 0) {
          console.warn('No valid invoices to share');
          return;
        }

        // Add each invoice to the ZIP
        validInvoices.forEach(({ blob, fileName }) => {
          zip.file(fileName, blob!);
        });

        // Generate the ZIP file
        const zipBlob = await zip.generateAsync({
          type: 'blob',
          compression: 'DEFLATE',
          compressionOptions: {
            level: 6, // Compression level (1-9)
          },
        });

        // Create a timestamp for the ZIP filename
        const timestamp = new Date().toISOString().split('T')[0]; // YYYY-MM-DD
        const zipFileName = `invoices_${timestamp}.zip`;

        if (zipBlob) {
          handleShare(zipBlob, zipFileName, [
            () => {
              setIsShareLoading(false);
            },
          ]);
        }
      } catch (error) {
        console.error('Failed to share bulk invoices:', error);
        setIsShareLoading(false);
        // Show user-friendly error
        if ((error as any)?.name !== 'AbortError') {
          // You can add your error toast/notification here
          console.error(
            'Share failed. Please try downloading the files instead.'
          );
        }
      }
    },
    [sellerWorkspaceId]
  );

  useEffect(() => {
    if (id) {
      updateBlobStateAndGetInvoiceBlob(id);
    }
  }, [id, sellerWorkspaceId]);

  return {
    invoiceDocBlob,
    handleInvoiceDownload,
    sendInvoiceLoading: sendInvoiceApiResponse?.isLoading,
    showSendInvoiceSuccessModal,
    setShowSendInvoiceSuccessModal,
    handleSendBulkInvoice,
    updateBlobStateAndGetInvoiceBlob,
    handleShareBulkInvoices,
    isShareLoading,
  };
};
